import {useMultiStyleConfig, BoxProps, StackProps, FlexProps, TextProps, VStack, Box, Heading, Text, Flex} from '@chakra-ui/react'
import {IconProps, CtaButton, ScrollDownButton} from '_uiComponents'
import {useAnalytics, useIsElementInViewport, useOpenDrawer} from '_customHooks'
import React, {useEffect, useRef, useState} from 'react'
import {PAGE_DESIGNER_MODULES} from '_utils'

interface TitleTextCtaProps {
    eyelet?: string
    title?: string
    description?: string

    ctaText?: string
    ctaUrl?: string
    ctaTargetBlank?: boolean

    cta2Text?: string
    cta2Url?: string
    cta2TargetBlank?: boolean

    ctaStyle?: 'text' | 'button'
    fullHeight?: boolean
    textAlign?: 'left' | 'center' | 'right'
    horizontalPosition?: 'left' | 'center' | 'right'
    contentColor?: string
    contentBackground?: string

    scrollDownArrow?: boolean
    ctaTextAnimation?: boolean
    textAnimation?: boolean

    classes?: string
    extraCSS?: string
    analyticsList?: string
    position: number
    analyticsID?: string
    analyticsName?: string
}

/**
 * TitleTextCta component renders a text section that can display an eyelet, title, description, one or two CTAs, and a scroll down arrow.
 *
 * @param {string} [eyelet] - Optional short label or eyelet displayed above the title.
 * @param {string} [title] - The main title text to display.
 * @param {string} [description] - Optional description text below the title.
 * @param {string} [ctaText] - Text for the primary CTA button.
 * @param {string} [ctaUrl] - URL for the primary CTA button.
 * @param {boolean} [ctaTargetBlank] - If true, opens the primary CTA link in a new tab.
 * @param {string} [cta2Text] - Text for the secondary CTA button.
 * @param {string} [cta2Url] - URL for the secondary CTA button.
 * @param {boolean} [cta2TargetBlank] - If true, opens the secondary CTA link in a new tab.
 * @param {'text' | 'button'} [ctaStyle] - Visual style variant for the CTA buttons.
 * @param {boolean} [fullHeight] - If true the component takes the full height of its container.
 * @param {'left' | 'center' | 'right'} [textAlign] - Text alignment for the content.
 * @param {'left' | 'center' | 'right'} [horizontalPosition] - Horizontal positioning of the content.
 * @param {string} [contentColor] - The content text color.
 * @param {string} [contentBackground] - The content background color.
 * @param {boolean} [scrollDownArrow] - If true, displays a scroll down arrow button.
 * @param {boolean} [ctaTextAnimation] - If true, enables animation for CTA button text.
 * @param {boolean} [textAnimation] - If true, enables animation for title text.
 * @param {string} [classes] - Additional CSS classes to apply to the component.
 * @param {string} [extraCSS] - Additional CSS styles to apply to the component.
 * @param {number} [position] - The position of the element with respect to other page designer elements in the page.
 * @param {string} props.analyticsID - The analytics id to send for the tracking
 *
 * @returns {JSX.Element} The rendered TitleTextCta component.
 */
const TitleTextCta = ({
    eyelet,
    title,
    description,
    ctaText,
    ctaUrl,
    ctaTargetBlank,
    cta2Text,
    cta2Url,
    cta2TargetBlank,
    ctaStyle,
    fullHeight,
    textAlign,
    horizontalPosition,
    contentColor = 'color.text.black',
    contentBackground = 'color.text.white',
    scrollDownArrow,
    ctaTextAnimation,
    textAnimation,
    classes,
    extraCSS,
    position,
    analyticsID,
    analyticsName,
}: TitleTextCtaProps): JSX.Element => {
    const ctaNum = Number(Boolean(ctaText && ctaUrl)) + Number(Boolean(cta2Text && cta2Url))
    const styles = useMultiStyleConfig('TitleTextCta', {
        textAlign,
        horizontalPosition,
        contentColor,
        contentBackground,
        textAnimation,
        fullHeight,
        ctaNum,
    }) as Record<string, BoxProps & StackProps & FlexProps & TextProps & IconProps>

    const {analyticsEventPromoView, analyticsEventPromoClick} = useAnalytics()
    const {handleShopTheImageClick} = useOpenDrawer()
    const targetElement = useRef(null)
    const isVisible = useIsElementInViewport(targetElement)
    const [alreadySent, setAlreadySent] = useState(false)
    const animationTime = '1000'

    const triggerAnalyticsEventPromoClick = (textBtn: string, positionBtn?: string): void => {
        analyticsEventPromoClick({
            id: analyticsID ? analyticsID : 'null',
            position: positionBtn ? String(position) + '-' + positionBtn : String(position),
            creative: PAGE_DESIGNER_MODULES.TITLE_TEXT_CTA,
            promoText: textBtn,
            name: analyticsName ? analyticsName : 'null',
        })
    }

    useEffect(() => {
        if (isVisible && !alreadySent) {
            analyticsEventPromoView({
                id: analyticsID ? analyticsID : 'null',
                position: position,
                creative: PAGE_DESIGNER_MODULES.DOUBLE_SAME_CLOUDINARY,
                name: analyticsName ? analyticsName : 'null',
            })
            setAlreadySent(true)
        }
    }, [isVisible])

    return (
        <Box {...styles.wrapper} css={extraCSS} data-testid="title-text-cta">
            <VStack {...styles.container} className={`${classes} cc-pd cc-pd-title-text-cta`}>
                {eyelet && (
                    <Text {...styles.eyelet} className="cc-eyelet">
                        {eyelet}
                    </Text>
                )}
                {title && (
                    <Text as="h2" {...styles.title} className="cc-title">
                        {title}
                    </Text>
                )}
                {description && (
                    <Text {...styles.text} className="cc-description">
                        {description}
                    </Text>
                )}

                {((ctaText && ctaUrl) || (cta2Text && cta2Url)) && (
                    <Flex {...styles.ctaContainer} className="cc-content-buttons" data-testid="title-text-ctas">
                        {ctaText && ctaUrl && (
                            <CtaButton
                                variant={ctaStyle}
                                href={ctaUrl}
                                isAnimated={ctaTextAnimation}
                                animationTime={animationTime}
                                openInNewTab={ctaTargetBlank}
                                ctaColor={contentColor}
                                onClick={(e) => {
                                    if (handleShopTheImageClick(e, ctaUrl)) return
                                    triggerAnalyticsEventPromoClick(ctaText, cta2Text && cta2Url ? '1' : '')
                                }}
                            >
                                {ctaText}
                            </CtaButton>
                        )}
                        {cta2Text && cta2Url && (
                            <CtaButton
                                variant={ctaStyle}
                                href={cta2Url}
                                isAnimated={ctaTextAnimation}
                                animationTime={animationTime}
                                openInNewTab={cta2TargetBlank}
                                ctaColor={contentColor}
                                onClick={(e) => {
                                    if (handleShopTheImageClick(e, cta2Url)) return
                                    triggerAnalyticsEventPromoClick(cta2Text, ctaText && ctaUrl ? '2' : '')
                                }}
                            >
                                {cta2Text}
                            </CtaButton>
                        )}
                    </Flex>
                )}

                {scrollDownArrow && (
                    <ScrollDownButton parentDataTestId="title-text-cta" dataTestId="title-text-cta-scroll-down" ariaLabel="Button scroll down title text cta" />
                )}
            </VStack>
        </Box>
    )
}

export {TitleTextCta}
